// src/pages/appointment-calendar/components/AppointmentSidebar.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import PhoneAutocomplete from '../../../components/ui/PhoneAutocomplete';
import Select from '../../../components/ui/Select';
import api from '../../../lib/api';
import { listTimeOff } from '../../../lib/timeOff';

const AppointmentSidebar = ({
  isOpen,
  onClose,
  selectedStaff,
  onStaffChange,
  onCreateAppointment
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    clientName: '',
    clientPhone: '',
	  services: [],
    staff: '',
    location: '',
    date: '',
    time: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [pickedClientId, setPickedClientId] = useState(null);
  // Numele a fost completat automat după telefon, deci nu mai e valabil dacă se schimbă numărul.
  const nameAutoFilledRef = useRef(false);


  const [staffOptions, setStaffOptions] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);

  const [loadingStaff, setLoadingStaff] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // încărcare opțiuni reale
  useEffect(() => {
    const bid = localStorage.getItem('current_business_id') || '1';
    const headers = { 'X-Business-Id': bid };

    // staff
    setLoadingStaff(true);
    api.get('/b/staff')
      .then(({ data }) => {
        const opts = (Array.isArray(data) ? data : []).map(u => ({
          value: String(u.id),
          label: u.label || u.username || `#${u.id}`,
        }));
        setStaffOptions(opts);
      })
      .catch(() => setStaffOptions([]))
      .finally(() => setLoadingStaff(false));

    // services
    setLoadingServices(true);
    api.get(`/b/${bid}/services/`)
      .then(({ data }) => {
        const list = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
        const opts = list.map(s => ({
          value: String(s.id),
          label: s.name || `Service #${s.id}`,
          duration: Number(s.duration_minutes) || 30,
        }));
        setServiceOptions(opts);
      })
      .catch(() => setServiceOptions([]))
      .finally(() => setLoadingServices(false));

    // locations
    setLoadingLocations(true);
    api.get('/b/locations', { headers })
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        const opts = list.map(l => ({ value: String(l.id), label: l.name || `Loc #${l.id}` }));
        setLocationOptions(opts);
        if (list.length === 1 && list[0]?.id != null) {
          setFormData(prev => ({ ...prev, location: String(list[0].id) }));
        }
      })
      .catch(() => setLocationOptions([]))
      .finally(() => setLoadingLocations(false));
  }, []);

  // verificare concediu pentru staff + dată selectată
  const [staffOnLeaveWarning, setStaffOnLeaveWarning] = useState(false);
  useEffect(() => {
    const staffId = formData.staff;
    const date = formData.date;
    if (!staffId || !date) { setStaffOnLeaveWarning(false); return; }
    let cancelled = false;
    listTimeOff({ status: "approved", staff_id: staffId, from: date, to: date })
      .then((list) => {
        if (cancelled) return;
        const d = new Date(date); d.setHours(12, 0, 0, 0);
        const onLeave = (list || []).some((r) => {
          const s = new Date(r.start_at || r.start_date); s.setHours(0, 0, 0, 0);
          const e = new Date(r.end_at || r.end_date); e.setHours(23, 59, 59, 999);
          return d >= s && d <= e;
        });
        setStaffOnLeaveWarning(onLeave);
      })
      .catch(() => { if (!cancelled) setStaffOnLeaveWarning(false); });
    return () => { cancelled = true; };
  }, [formData.staff, formData.date]);

  // map id->durata
  const serviceDurations = useMemo(() => {
    const map = {};
    for (const s of serviceOptions) map[s.value] = s.duration ?? 30;
    return map;
  }, [serviceOptions]);

  const timeSlots = [
    { value: '09:00', label: '09:00' }, { value: '09:30', label: '09:30' },
    { value: '10:00', label: '10:00' }, { value: '10:30', label: '10:30' },
    { value: '11:00', label: '11:00' }, { value: '11:30', label: '11:30' },
    { value: '12:00', label: '12:00' }, { value: '12:30', label: '12:30' },
    { value: '13:00', label: '13:00' }, { value: '13:30', label: '13:30' },
    { value: '14:00', label: '14:00' }, { value: '14:30', label: '14:30' },
    { value: '15:00', label: '15:00' }, { value: '15:30', label: '15:30' },
    { value: '16:00', label: '16:00' }, { value: '16:30', label: '16:30' },
    { value: '17:00', label: '17:00' }, { value: '17:30', label: '17:30' },
    { value: '18:00', label: '18:00' },
  ];

  const handleInputChange = (field, value) => {
    const clearAutoFilledName = field === 'clientPhone' && nameAutoFilledRef.current;
    if (field === 'clientName' || field === 'clientPhone') {
      nameAutoFilledRef.current = false;
      setPickedClientId(null);
    }
    setFormData(prev => ({
      ...prev,
      [field]: value,
      ...(clearAutoFilledName ? { clientName: '' } : {}),
    }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handlePickPhoneClient = (c) => {
    setFormData(prev => ({
      ...prev,
      clientPhone: c.phone || prev.clientPhone,
      clientName: c.name || prev.clientName,
    }));
    setPickedClientId(c.id ?? null);
    nameAutoFilledRef.current = false;
    setErrors(prev => ({ ...prev, clientName: '', clientPhone: '' }));
  };

  // Numărul aparține unui singur client. Nu scriem peste un nume introdus deja: poate fi altă persoană.
  const handleExactPhoneMatch = (c) => {
    if (String(formData?.clientName || '').trim()) return false;
    handlePickPhoneClient(c);
    nameAutoFilledRef.current = true;
    return true;
  };

  const resetClientPick = () => {
    setPickedClientId(null);
    nameAutoFilledRef.current = false;
  };

  const validateForm = () => {
    const e = {};
    if (!formData?.clientName?.trim()) e.clientName = t('appointments.clientNameRequired');
    if (!formData?.clientPhone?.trim()) e.clientPhone = t('appointments.clientPhoneRequired');
    if (!Array.isArray(formData?.services) || formData.services.length === 0)
      e.services = t('appointments.servicesRequired');
    if (!formData?.staff) e.staff = t('appointments.staffRequired');
    if (!formData?.location) e.location = t('appointments.locationRequired');
    if (!formData?.date) e.date = t('appointments.dateRequired');
    if (!formData?.time) e.time = t('appointments.timeRequired');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const bid = localStorage.getItem('current_business_id') || '1';

      const startLocal = new Date(`${formData.date}T${formData.time}`);
      const serviceIds = (Array.isArray(formData.services) ? formData.services : [])
        .map((x) => Number(x))
        .filter((n) => Number.isFinite(n));
      const primaryServiceId = serviceIds.length ? serviceIds[0] : null;
      const dur = (Array.isArray(formData.services) ? formData.services : [])
        .map((sid) => serviceDurations[String(sid)] ?? 30)
        .reduce((a, b) => a + b, 0) || 30;
      const endLocal = new Date(startLocal.getTime() + dur * 60 * 1000);

      const payload = {
        // client real (fără client_id -> backend creează/reciclează după phone)
        ...(pickedClientId ? { client_id: pickedClientId } : {}),
        client_name: formData.clientName,
        client_phone: formData.clientPhone,

        // id-uri numerice
        service: primaryServiceId,
        services: serviceIds,
        staff: Number(formData.staff),
        location_id: Number(formData.location),

        // timp
        start: startLocal.toISOString(),
        end: endLocal.toISOString(),

        // status pe enum-ul din model
        status: 'booked',

        notes: formData.notes || '',
      };

      const { data } = await api.post(
        '/b/appointments',
        payload,
        { headers: { 'X-Business-Id': bid } }
      );

      window.dispatchEvent(new CustomEvent('appointments:refetch'));
      onCreateAppointment?.(data || payload);

      // reset
      resetClientPick();
      setFormData({
        clientName: '',
        clientPhone: '',
        services: [],
        staff: '',
        location: '',
        date: '',
        time: '',
        notes: ''
      });
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data
        ? JSON.stringify(err.response.data)
        : (err?.message || t('appointments.errorSave'));
      setErrors(prev => ({ ...prev, form: msg }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    resetClientPick();
    setFormData({
      clientName: '',
      clientPhone: '',
      services: [],
      staff: '',
      location: '',
      date: '',
      time: '',
      notes: ''
    });
    setErrors({});
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Mobile Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={onClose} />
      {/* Sidebar */}
      <div className={`fixed right-0 top-16 bottom-0 w-full max-w-md bg-surface border-l border-border z-50 transform transition-transform duration-300 ease-out ${isOpen ? 'translate-x-0' : 'translate-x-full'} lg:relative lg:top-0 lg:translate-x-0 lg:w-80`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">{t('appointments.newTitle')}</h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="lg:hidden" type="button">
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <div className="p-6 overflow-y-auto h-full">
          {errors?.form && <div className="mb-4 text-sm text-destructive break-words">{errors.form}</div>}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Client */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground">{t('appointments.clientInfo')}</h3>

              <Input
                label={t('appointments.clientNameLabel')}
                type="text"
                placeholder={t('appointments.clientNamePlaceholder')}
                value={formData?.clientName}
                onChange={(e) => handleInputChange('clientName', e?.target?.value)}
                error={errors?.clientName}
                required
              />

              <PhoneAutocomplete
                label={t('common.phone')}
                placeholder="0712 345 678"
                value={formData?.clientPhone}
                onChange={(e) => handleInputChange('clientPhone', e?.target?.value)}
                onPickClient={handlePickPhoneClient}
                onExactMatch={handleExactPhoneMatch}
                error={errors?.clientPhone}
                required
              />
            </div>

	            {/* Servicii */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground">{t('appointments.servicesSection')}</h3>
              <Select
                label={t('appointments.selectServicesLabel')}
                options={serviceOptions}
	                multiple
	                value={formData?.services}
	                onChange={(value) => handleInputChange('services', value)}
	                error={errors?.services}
                required
                searchable
                loading={loadingServices}
                placeholder={loadingServices ? t('common.loading') : t('appointments.chooseServices')}
              />
            </div>

            {/* Locație + Staff + Oră/Dată */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground">{t('appointments.schedulingSection')}</h3>

              <Select
                label={t('appointments.locationLabel')}
                options={locationOptions}
                value={formData?.location}
                onChange={(value) => handleInputChange('location', value)}
                error={errors?.location}
                required
                loading={loadingLocations}
                placeholder={loadingLocations ? t('common.loading') : t('appointments.chooseLocation')}
              />

              <Select
                label={t('appointments.staffLabel')}
                options={staffOptions}
                value={formData?.staff}
                onChange={(value) => handleInputChange('staff', value)}
                error={errors?.staff}
                required
                loading={loadingStaff}
                placeholder={loadingStaff ? t('common.loading') : t('appointments.chooseStaff')}
              />

              {staffOnLeaveWarning && (
                <div className="flex items-center gap-2 rounded-lg border border-orange-300 bg-orange-50 dark:bg-orange-950/30 px-3 py-2 text-sm text-orange-700 dark:text-orange-400">
                  <Icon name="AlertTriangle" size={16} className="shrink-0" />
                  {t('appointments.staffOnLeaveWarning')}
                </div>
              )}

              <Input
                label={t('appointments.selectDate')}
                type="date"
                value={formData?.date}
                onChange={(e) => handleInputChange('date', e?.target?.value)}
                error={errors?.date}
                required
                min={new Date()?.toISOString()?.split('T')?.[0]}
              />

              <Select
                label={t('appointments.selectTime')}
                options={timeSlots}
                value={formData?.time}
                onChange={(value) => handleInputChange('time', value)}
                error={errors?.time}
                required
              />
            </div>

            {/* Observații */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground">{t('appointments.notesSection')}</h3>
              <textarea
                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                rows="3"
                placeholder={t('appointments.notesPlaceholder')}
                value={formData?.notes}
                onChange={(e) => handleInputChange('notes', e?.target?.value)}
              />
            </div>

            {/* Actions */}
            <div className="flex space-x-3 pt-4">
              <Button type="submit" variant="default" className="flex-1" disabled={isLoading}>
                <Icon name="Check" size={16} className="mr-2" />
                {isLoading ? t('appointments.saving') : t('appointments.createAppointment')}
              </Button>

              <Button type="button" variant="outline" onClick={handleReset} disabled={isLoading}>
                <Icon name="RotateCcw" size={16} />
              </Button>
            </div>
          </form>

          {/* Filtrare angajați (calendar) */}
          <div className="mt-8 pt-6 border-t border-border">
            <h3 className="text-sm font-medium text-foreground mb-4">{t('appointments.filterStaff')}</h3>
            <Select
              label={t('appointments.showScheduleFor')}
              options={[{ value: 'all', label: t('calendar.allStaff') }, ...staffOptions]}
              value={selectedStaff || 'all'}
              onChange={(val) => onStaffChange?.(val || 'all')}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default AppointmentSidebar;
